import type { Session } from '@colplat/core-client';
import { fieldKey, type PresenceUser, type Uuid } from '@colplat/protocol';
import { escapeHtml } from './dom.js';
import { describeFieldState } from './state-visuals.js';
import { historyButtonHtml, historyMarkup } from './fieldHistory.js';
import type { MountedField } from './collabField.js';

/**
 * Compare-and-swap toggle (ADR-0007) — no lease dance: `Session.toggle()`
 * sends a `write` with no `leaseId` directly. A toggle's state therefore
 * never practically reaches `mine`/`locked`/`claiming` (those states are only
 * ever set from `leaseTaken`/`granted` messages, which `sync` never sends for
 * a non-pessimistic field) — routed through the same `describeFieldState` as
 * `CollabField` anyway, for one shared state-mapping rather than two.
 */

export interface CollabToggleOptions {
  session: Session;
  entity: string;
  recordId: Uuid;
  field: string;
  label: string;
}

export function mountCollabToggle(container: HTMLElement, opts: CollabToggleOptions): MountedField {
  const key = fieldKey(opts.entity, opts.recordId, opts.field);
  let historyOpen = false;

  function resolveUser(id: Uuid): PresenceUser | undefined {
    return opts.session.presence(opts.entity, opts.recordId).find((u) => u.id === id);
  }

  function markup(): string {
    const read = opts.session.field(opts.entity, opts.recordId, opts.field);
    const review = opts.session.pendingReviews(opts.entity, opts.recordId).find((r) => r.key === key);
    const visual = describeFieldState(read.state, resolveUser);
    const checked = read.value && read.value.t === 'bool' ? read.value.v : false;
    const ariaLabel = `${escapeHtml(opts.label)}${visual.aria ? `. ${escapeHtml(visual.aria)}` : ''}`;

    const historyPanel = historyOpen
      ? `<div class="cf-hist show" data-history-panel>${historyMarkup(opts.label, read.history, review, resolveUser)}</div>`
      : '<div class="cf-hist" data-history-panel></div>';

    return `<div class="cf-f cf-f--toggle ${visual.cls}" data-key="${key}">
      <input class="form-check-input m-0" type="checkbox" role="switch" data-toggle-input
        ${checked ? 'checked' : ''} ${visual.readOnly ? 'disabled' : ''} aria-label="${ariaLabel}">
      <label>${escapeHtml(opts.label)}</label>
      <span class="cf-f__state" role="status">${visual.chipHtml}</span>
      <span class="cf-f__tools">${historyButtonHtml(read.history.length, opts.label)}</span>
      ${historyPanel}
    </div>`;
  }

  function wire(): void {
    container.querySelector<HTMLInputElement>('[data-toggle-input]')?.addEventListener('change', () => {
      void opts.session.toggle(opts.entity, opts.recordId, opts.field);
    });
    container.querySelector<HTMLButtonElement>('[data-history-toggle]')?.addEventListener('click', () => {
      historyOpen = !historyOpen;
      render();
    });
    container.querySelectorAll<HTMLButtonElement>('[data-review-pick]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const keep = btn.dataset['reviewPick'] as 'mine' | 'theirs';
        void opts.session.resolveReview(key, keep);
      });
    });
  }

  function render(): void {
    container.innerHTML = markup();
    wire();
  }

  const unsubscribe = opts.session.subscribe(render);
  render();
  return { refresh: render, destroy: unsubscribe };
}
